import { useEffect, useState } from 'react'
import { Activity, Thermometer, Zap } from 'lucide-react'
import { api } from '../../utils/api'

interface Metric {
  temp_c?: number
  power_w?: number
  cpu_utilization?: number
}

interface LivingTwinOverlayProps {
  rackId: string | number
}

export function LivingTwinOverlay({ rackId }: LivingTwinOverlayProps) {
  const [metrics, setMetrics] = useState<Record<string, Metric>>({})
  const [status, setStatus] = useState<'CONNECTING' | 'LIVE' | 'OFFLINE'>('CONNECTING')

  useEffect(() => {
    setStatus('CONNECTING')
    const sse = new EventSource(
      `${api.defaults.baseURL || ''}/api/telemetry/stream?rack_id=${rackId}`,
      { withCredentials: true }
    )

    
    sse.onopen = () => {
      setStatus('LIVE')
    }

    sse.addEventListener('metrics_update', (e) => {
      try {
        const data = JSON.parse(e.data)
        setMetrics(prev => ({ ...prev, ...data }))
      } catch {
        // Ignore malformed telemetry frames and keep the last valid metrics.
      }
    })
    
    sse.onerror = () => {
      setStatus('OFFLINE')
      sse.close()
    }

    return () => {
      sse.close()
    }
  }, [rackId])

  // Aggregate metrics
  const totalPower = Object.values(metrics).reduce((sum, m) => sum + (m.power_w || 0), 0)
  const maxTemp = Math.max(0, ...Object.values(metrics).map(m => m.temp_c || 0))

  return (
    <div className="absolute top-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
      <div className="bg-bg-panel/80 backdrop-blur-md border border-border/50 rounded-xl p-3 shadow-2xl pointer-events-auto flex flex-col gap-3 min-w-[200px]">
        
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border/50 pb-2">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-accent" />
            <span className="font-bold text-sm tracking-wide">LIVING TWIN</span>
          </div>
          <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold ${
            status === 'LIVE' ? 'bg-status-normal/10 text-status-normal border border-status-normal/20' :
            status === 'CONNECTING' ? 'bg-status-warning/10 text-status-warning border border-status-warning/20' :
            'bg-status-critical/10 text-status-critical border border-status-critical/20'
          }`}>
            {status === 'LIVE' && <span className="w-1.5 h-1.5 rounded-full bg-status-normal animate-pulse" />}
            {status}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-bg-input/50 rounded-lg p-2 flex flex-col items-center justify-center">
            <Zap className="w-4 h-4 text-yellow-400 mb-1" />
            <span className="text-xs text-text-muted">Total Power</span>
            <span className="font-mono font-bold text-text-primary">{totalPower.toFixed(0)} W</span>
          </div>
          <div className="bg-bg-input/50 rounded-lg p-2 flex flex-col items-center justify-center">
            <Thermometer className="w-4 h-4 text-orange-400 mb-1" />
            <span className="text-xs text-text-muted">Max Temp</span>
            <span className="font-mono font-bold text-text-primary">{maxTemp > 0 ? `${maxTemp.toFixed(1)}°C` : '--'}</span>
          </div>
        </div>

      </div>
    </div>
  )
}
